import { Button, DatePicker, Drawer, Select, Space, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useState } from "react";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { useGetBankTransactionsQuery } from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import {
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
} from "../../../features/cashCollection/geoSetup/geoSetupApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import {
  GetTransactionsSummaryDataParamsType,
  GetTransactionsSummaryDataType,
} from "../../../types/cashCollection/cashCollectionSetup";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, formatNumber } from "../../../utils/utils";
import ReconcileModal from "./ReconcileModal";

export default function BankTransactionList() {
  const [open, setOpen] = useState(false);
  const { RangePicker } = DatePicker;
  const { open: config, handleOpen, handleClose } = useModal<any>();
  const { page, limit, handlePagination, handleLimit, filter, handleFilter } =
    useTable<GetTransactionsSummaryDataParamsType>();

  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(
    filter?.zoneId ?? null
  );
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(
    filter?.areaId ?? null
  );
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | null>(
    filter?.territoryId ?? null
  );
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(
    filter?.routeId ?? null
  );

  const { isError, error, isLoading, data } = useGetBankTransactionsQuery({
    params: {
      ...filter,
      skip: undefined,
      page,
      limit,
    },
  });

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        areaId: selectedAreaId ? selectedAreaId : undefined,
      },
    },
    {
      skip: !selectedAreaId,
    }
  );

  // Get all Routes
  const {
    isError: getAllRouteIsError,
    error: getAllRouteError,
    data: getAllRouteData,
  } = useGetAllRouteQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  useError(isError, error, "Get transaction list fail");
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(getAllRouteIsError, getAllRouteError, "Get all route fail");

  // const showDrawer = () => {
  //   setOpen(true);
  // };

  const onClose = () => {
    setOpen(false);
  };

  const columns: ColumnsType<GetTransactionsSummaryDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap">Field Officer</div>,
      dataIndex: "user",
      align: "center",
      render: (val) => {
        return val?.username ? capitalize(val?.username) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "route",
      align: "center",
      render: (val) => {
        return val?.zone?.zoneName ? capitalize(val?.zone?.zoneName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Area</div>,
      dataIndex: "route",
      align: "center",
      render: (val) => {
        return val?.area?.areaName ? capitalize(val?.area?.areaName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Territory</div>,
      dataIndex: "route",
      align: "center",
      render: (val) => {
        return val?.territory?.territoryName
          ? capitalize(val?.territory?.territoryName)
          : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Route</div>,
      dataIndex: "route",
      align: "center",
      render: (val) => {
        return val?.routeName ? capitalize(val?.routeName) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Deposite Amount (BDT)</div>,
      dataIndex: "amount",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: <div className="text-nowrap">Transaction ID</div>,
      dataIndex: "transactionId",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Transaction Date</div>,
      dataIndex: "createdDate",
      align: "center",
      render: (val) => {
        return val ? formatDate(val) : "N/A";
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      render: (val, record) => (
        <div className="flex justify-center items-center gap-2">
          <Tooltip title="Reconcile">
            <Button
              onClick={() => handleOpen({ type: "ADD", data: record })}
              type="primary"
            >
              Reconcile
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <SidebarLayout title="Bank Transactions">
        <CustomTable
          page={page}
          limit={limit}
          columns={columns}
          loading={isLoading}
          total={data?.data?.count}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          dataSource={addKeyInArray(data?.data?.rows)}
          filterSection={
            <>
              {/* Route */}
              <Select
                allowClear
                showSearch
                style={{ width: 150 }}
                placeholder="Route"
                className="w-full"
                suffixIcon={<SelectDownIcon />}
                options={
                  getAllRouteData?.data?.rows?.map((val: any) => ({
                    label: capitalize(val?.routeName),
                    value: val?.id,
                  })) || []
                }
                onChange={(value) => handleFilter("routeId", value)}
              />
              <RangePicker
                allowClear
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    handleFilter("dateFrom", dates[0].format("YYYY-MM-DD"));
                    handleFilter(
                      "dateTo",
                      dates[1].add(1, "day").format("YYYY-MM-DD")
                    );
                  } else {
                    // reset if cleared
                    handleFilter("dateFrom", undefined);
                    handleFilter("dateTo", undefined);
                  }
                }}
              />

              {/* <IconButton
                className="flex items-center text-[#7a8799]"
                icon={<FilterIconThree />}
                onClick={() => showDrawer()}
              >
                Filter
              </IconButton> */}
            </>
          }
        />
      </SidebarLayout>
      <ReconcileModal
        data={config.data}
        open={config.open}
        handleClose={handleClose}
      />

      <Drawer open={open} width={400} onClose={onClose}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          Filter
        </div>

        <div>
          <Select
            placeholder="Select Zone"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-8"
            options={
              getAllZoneData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.zoneName),
                value: val?.id,
              })) || []
            }
            value={selectedZoneId}
            onChange={(value) => {
              setSelectedZoneId(value ?? null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          <Select
            placeholder="Select Area"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedZoneId}
            options={
              getAllAreaData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.areaName),
                value: val?.id,
              })) || []
            }
            value={selectedAreaId}
            onChange={(value) => {
              setSelectedAreaId(value ?? null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
          />

          <Select
            placeholder="Select Territory"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedAreaId}
            options={
              getAllTerritoryData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.territoryName),
                value: val?.id,
              })) || []
            }
            value={selectedTerritoryId}
            onChange={(value) => {
              setSelectedTerritoryId(value ?? null);
              setSelectedRouteId(null);
            }}
          />

          <Select
            placeholder="Route"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedTerritoryId}
            options={
              getAllRouteData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.routeName),
                value: val?.id,
              })) || []
            }
            value={selectedRouteId}
            onChange={(value) => setSelectedRouteId(value ?? null)}
          />
        </div>

        <Space className="w-full mt-8" size={12}>
          <Button
            className="normal-dark-button modal-button !h-[40px]"
            onClick={() => {
              setSelectedZoneId(null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
              handleFilter("zoneId", undefined);
              handleFilter("areaId", undefined);
              handleFilter("territoryId", undefined);
              handleFilter("routeId", undefined);
              // handleFilter("userId", undefined);
              onClose();
            }}
            size="middle"
          >
            Cancel
          </Button>

          <Button
            className="modal-button !h-[40px]"
            type="primary"
            size="middle"
            onClick={() => {
              handleFilter("zoneId", selectedZoneId ?? undefined);
              handleFilter("areaId", selectedAreaId ?? undefined);
              handleFilter("territoryId", selectedTerritoryId ?? undefined);
              handleFilter("routeId", selectedRouteId ?? undefined);

              onClose();
            }}
          >
            Filter
          </Button>
        </Space>
      </Drawer>
    </>
  );
}
